import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { Switch } from "@shared/ui/switch";
import { Input } from "@shared/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { Alert, AlertDescription } from "@shared/ui/alert";
import {
  Pencil,
  Plus,
  Search,
  Calendar,
  User,
  Check,
  X,
  Shield,
  Loader2,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useToast } from "@shared/hooks/use-toast";
import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useSafetyPackages,
  useCreateSafetyPackage,
  useUpdateSafetyPackage,
  useToggleSafetyPackageStatus,
  useSafetyProtocols,
  usePPEEquipment,
} from "../../../../hooks";
import { SafetyPackage } from "../../../../types";
import { logEvent } from "@shared/analytics/analytics";

// ====================== TYPES ======================

interface SafetyPackageFormData {
  name: string;
  description: string;
  selectedProtocols: number[];
  selectedPPE: number[];
}

// ====================== HOOKS ======================

// Custom hook for debounced search
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// ====================== COMPONENTS ======================

// Safety Package card component for better reusability
const SafetyPackageCard: React.FC<{
  package: SafetyPackage;
  onEdit: (pkg: SafetyPackage) => void;
  onToggleActive: (pkg: SafetyPackage) => void;
  isUpdating: boolean;
  currentPackage: SafetyPackage | null;
}> = ({ package: pkg, onEdit, onToggleActive, isUpdating, currentPackage }) => {
  return (
    <Card className="overflow-hidden transition-all hover:shadow-md hover:border-primary/20 group">
      <CardContent className="p-0">
        <div className="p-4 border-b bg-muted/20">
          <div className="flex justify-between items-start">
            <div>
              <h5 className="font-medium">{pkg.name}</h5>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {pkg.description}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(pkg)}
              className="opacity-0 group-hover:opacity-100 hover:bg-background/80"
              disabled={isUpdating}
            >
              {isUpdating && currentPackage?.id === pkg.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Pencil className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="p-4">
          <div className="flex gap-2 items-center text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            <p>{pkg.createdUserRemoteName}</p>
          </div>
          <div className="flex gap-2 items-center text-sm text-muted-foreground mt-1">
            <Calendar className="h-3.5 w-3.5" />
            <p>{pkg.createdDate}</p>
          </div>

          <div className="flex items-center justify-between mt-4 pt-4 border-t">
            <span className="text-sm">
              {pkg.isActive ? (
                <span className="flex items-center gap-1.5 text-green-600 bg-green-50 dark:bg-green-950/30 px-2 py-1 rounded-full text-xs font-medium">
                  <Check className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-gray-600 bg-gray-50 dark:bg-gray-800/30 px-2 py-1 rounded-full text-xs font-medium">
                  <X className="h-3 w-3" /> Inactive
                </span>
              )}
            </span>
            <Switch
              checked={pkg.isActive}
              onCheckedChange={() => onToggleActive(pkg)}
              disabled={isUpdating}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Empty state component
const EmptyState: React.FC<{
  hasSearchQuery: boolean;
  onAddNew: () => void;
}> = ({ hasSearchQuery, onAddNew }) => {
  return (
    <div className="col-span-full py-12 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
      <div className="flex flex-col items-center gap-2">
        <Shield className="h-10 w-10 text-muted-foreground/50" />
        <h4>No safety packages found</h4>
        <p className="text-sm">
          {hasSearchQuery
            ? "Try adjusting your search or add a new safety package"
            : "No safety packages available"}
        </p>
        <Button variant="outline" className="mt-2" onClick={onAddNew}>
          <Plus className="mr-2 h-4 w-4" /> Add New Package
        </Button>
      </div>
    </div>
  );
};

// ====================== MAIN COMPONENT ======================

const SafetyPackagesTab: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<SafetyPackage | null>(
    null
  );

  // Get search query from URL
  const searchQuery = searchParams.get("search") || "";

  // Debounce the search input for better UX (but server handles the actual search)
  const [searchInput, setSearchInput] = useState(searchQuery);
  const debouncedSearchInput = useDebounce(searchInput, 500);

  // Update URL when debounced search changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (debouncedSearchInput.trim()) {
      newSearchParams.set("search", debouncedSearchInput.trim());
    } else {
      newSearchParams.delete("search");
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [debouncedSearchInput, searchParams, setSearchParams]);

  // API hooks - now using server-side search
  const { data: packages = [] } = useSafetyPackages(searchQuery);

  // Get safety protocols and PPE equipment data
  const { data: safetyProtocols = [] } = useSafetyProtocols();
  const { data: ppeEquipment = [] } = usePPEEquipment();

  const createPackageMutation = useCreateSafetyPackage();
  const updatePackageMutation = useUpdateSafetyPackage();
  const toggleStatusMutation = useToggleSafetyPackageStatus();

  // Transform API data into options format
  const protocolOptions = useMemo(() => {
    return safetyProtocols
      .filter((protocol) => protocol.isActive) // Only show active protocols
      .map((protocol) => ({
        label: protocol.name,
        value: protocol.id,
      }));
  }, [safetyProtocols]);

  const ppeOptions = useMemo(() => {
    return ppeEquipment
      .filter((equipment) => equipment.isActive) // Only show active equipment
      .map((equipment) => ({
        label: equipment.name,
        value: equipment.id,
      }));
  }, [ppeEquipment]);

  // Form fields configuration with dynamic options
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "name",
        label: "Package Name",
        type: "text",
        required: true,
        placeholder: "Enter safety package name",
        minLength: 3,
        maxLength: 100,
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Describe this safety package and its purpose",
        rows: 3,
        maxLength: 500,
        className: "pt-2",
      },
      {
        name: "selectedProtocols",
        label: "Safety Protocols",
        type: "multi-select",
        required: true,
        options: protocolOptions,
        placeholder: "Select safety protocols...",
        helperText:
          "Choose the protocols that should be included in this package",
        className: "pt-2",
      },
        {
          name: "selectedPPE",
          label: "Required PPE Equipment",
          type: "multi-select",
          required: true,
          options: ppeOptions,
          placeholder: "Select required PPE...",
          helperText: "Choose the PPE equipment required for this package",
          className: "pt-2",
        },
    ],
    [protocolOptions, ppeOptions]
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      name: "",
      description: "",
      selectedProtocols: [],
      selectedPPE: [],
    },
  });

  useEffect(() => {
    if (editingPackage && isEditModalOpen) {
      // Map protocols and equipment to the correct format for multi-select
      const selectedProtocolIds =
        editingPackage.protocols
          ?.map((protocol) => {
            if (typeof protocol === "string") {
              const foundProtocol = safetyProtocols.find(
                (p) => p.name === protocol
              );
              return foundProtocol?.id;
            }
            return protocol;
          })
          .filter(Boolean) || [];

      const selectedPPEIds =
        editingPackage.equipment
          ?.map((equipment) => {
            if (typeof equipment === "string") {
              const foundEquipment = ppeEquipment.find(
                (e) => e.name === equipment
              );
              return foundEquipment?.id;
            }
            return equipment;
          })
          .filter(Boolean) || [];

      const populateData = {
        name: editingPackage.name,
        description: editingPackage.description,
        selectedProtocols: selectedProtocolIds,
        selectedPPE: selectedPPEIds,
      };
      FormService.populateForm(form, populateData, formFields);
    } else if (isAddModalOpen) {
      const populateData = {
        name: "",
        description: "",
        selectedProtocols: [],
        selectedPPE: [],
      };
      FormService.populateForm(form, populateData, formFields);
    }
  }, [
    editingPackage,
    isEditModalOpen,
    isAddModalOpen,
    form,
    formFields,
    safetyProtocols,
    ppeEquipment,
  ]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {},
  []);

  // Handle form submission
  const handleSubmit = async (data: SafetyPackageFormData) => {
    try {
      if (editingPackage) {
        const updatePayload = {
          id: editingPackage.id,
          name: data.name.trim(),
          description: data.description.trim(),
          selectedProtocols: data.selectedProtocols || [],
          selectedPPE: data.selectedPPE || [],
        };
        // Update existing package
        await updatePackageMutation.mutateAsync(updatePayload, {
          onSuccess: () => {
            logEvent("Safety Package Updated", {
              id: editingPackage.id,
              name: data.name.trim(),
            })
          }
        });
        setIsEditModalOpen(false);
      } else {
        const createPayload = {
          name: data.name.trim(),
          description: data.description.trim(),
          selectedProtocols: data.selectedProtocols || [],
          selectedPPE: data.selectedPPE || [],
        };
        // Create new package
        await createPackageMutation.mutateAsync(createPayload, {
          onSuccess: () => {
            logEvent("Safety Package Created", {
              name: data.name.trim(),
            })
          }
        });
        setIsAddModalOpen(false);
      }

      FormService.resetForm(form, formFields);
      setEditingPackage(null);
    } catch (error) {}
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  // Handle edit
  const handleEdit = useCallback((pkg: SafetyPackage) => {
    setEditingPackage(pkg);
    setIsEditModalOpen(true);
  }, []);

  // Handle toggle status
  const handleToggleStatus = useCallback(
    async (pkg: SafetyPackage) => {
      try {
        await toggleStatusMutation.mutateAsync({
          id: pkg.id,
          isActive: !pkg.isActive,
          name: pkg.name,
        });
      } catch (error) {}
    },
    [toggleStatusMutation]
  );

  // Handle modal close
  const handleAddModalClose = useCallback(() => {
    setIsAddModalOpen(false);
    setEditingPackage(null);
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleEditModalClose = useCallback(() => {
    setIsEditModalOpen(false);
    setEditingPackage(null);
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  // Handle search input change
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(e.target.value);
    },
    []
  );

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-start flex-col pb-4">
          <h4 className="page-subheading">Safety Packages</h4>
          <p className="page-subtitle">Define and manage safety checklists.</p>
        </div>
        <Button
          onClick={() => setIsAddModalOpen(true)}
          disabled={createPackageMutation.isPending}
        >
          {createPackageMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Plus className="mr-2 h-4 w-4" />
          )}
          Add New Package
        </Button>
      </div>

      {/* Packages Grid */}
      <div className="relative pb-4">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search safety packages..."
              className="pl-8 w-full sm:w-[250px]"
              value={searchInput}
              onChange={handleSearchChange}
            />
          </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.isArray(packages) &&
          packages.map((pkg) => (
            <SafetyPackageCard
              key={pkg.id}
              package={pkg}
              onEdit={handleEdit}
              onToggleActive={handleToggleStatus}
              isUpdating={
                updatePackageMutation.isPending ||
                toggleStatusMutation.isPending
              }
              currentPackage={editingPackage}
            />
          ))}

        {Array.isArray(packages) && packages.length === 0 && (
          <EmptyState
            hasSearchQuery={!!searchQuery}
            onAddNew={() => setIsAddModalOpen(true)}
          />
        )}
      </div>

      {/* Add Package Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={handleAddModalClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Create New Safety Package
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={createPackageMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleAddModalClose}
                    disabled={createPackageMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={createPackageMutation.isPending}
                  >
                    {createPackageMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      "Create Package"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Package Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={handleEditModalClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Edit Safety Package
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pr-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleSubmit}
              onReset={handleReset}
              onFieldChange={handleFieldChange}
              loading={updatePackageMutation.isPending}
              config={{
                showResetButton: false,
                showSubmitButton: false,
                resetOnSubmit: false,
                classes: {
                  form: "space-y-4",
                  container: "space-y-2",
                  label: "block text-sm font-medium text-gray-900 mb-1",
                  input:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                  textarea:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical disabled:bg-gray-100 disabled:cursor-not-allowed",
                  select:
                    "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                  error: "text-sm text-red-600 mt-1",
                  helperText: "text-xs text-muted-foreground mt-1",
                  required: "text-red-500 ml-1",
                },
              }}
              renderCustomFooter={() => (
                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={handleEditModalClose}
                    disabled={updatePackageMutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={form.handleSubmit(handleSubmit)}
                    disabled={updatePackageMutation.isPending}
                  >
                    {updatePackageMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      "Update Package"
                    )}
                  </Button>
                </div>
              )}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SafetyPackagesTab;
